import type { ContainerNode } from "../layout/types.js";
import { applyPatchToContainerNode } from "./applyRuntimePatches.js";
import { collectBehaviorDependencies } from "./compileEvents.js";
import { compileBehavior } from "./compileBehavior.js";
import { compileNode } from "./compileNode.js";
import { evaluateWithRenderCache } from "./renderCache.js";
import {
  applyRuntimeDomTargetFlags,
  runCompiledNodeBehavior,
  withCurrentNodeGeometry,
} from "./applyNodeBehavior.js";
import { prepareAbsoluteContainerChildren } from "./renderClip.js";
import type {
  CompileNodeContext,
  CompiledNode,
  RenderContainerNode,
  RuntimeContext,
} from "./types.js";
import { compileNodeStyle, transitionsTouchTopLeft } from "./helpers.js";
import { uniqueDependencies } from "./method.js";

export function compileContainerNode(
  node: ContainerNode,
  compileContext: CompileNodeContext,
): CompiledNode {
  const compiledChildren = node.children
    .map((child) => compileNode(child, compileContext))
    .filter((child): child is CompiledNode => child !== null);
  const baseStyle = compileNodeStyle(node.style, node.transitions);
  const behavior = node.behavior
    ? compileBehavior(
        node.behavior,
        node.id,
        compileContext.catalogDeps,
        undefined,
        compileContext.layoutNodeById,
      )
    : undefined;

  const dependencies = uniqueDependencies([
    ...compiledChildren.flatMap((child) => child.dependencies),
    ...(behavior ? collectBehaviorDependencies(behavior, compileContext) : []),
  ]);

  return {
    id: node.id,
    dependencies,

    evaluate(ctx: RuntimeContext): RenderContainerNode | null {
      return evaluateWithRenderCache(ctx, node.id, dependencies, () => {
        const children = compiledChildren
          .map((child) => child.evaluate(ctx))
          .filter(
            (child): child is NonNullable<typeof child> => child !== null,
          );

        let renderNode: RenderContainerNode = {
          id: node.id,
          key: ctx.__nodeInstanceKeyPrefix
            ? `${ctx.__nodeInstanceKeyPrefix}${node.id}`
            : node.id,
          kind: "container",
          x: node.x,
          y: node.y,
          width: node.width,
          height: node.height,
          rotation: node.rotation ?? 0,
          zIndex: node.zIndex,
          layout: node.layout,
          flexDirection: node.flexDirection,
          gap: node.gap,
          justifyContent: node.justifyContent,
          alignItems: node.alignItems,
          flexWidthMode: node.flexWidthMode,
          flexHeightMode: node.flexHeightMode,
          style: { ...baseStyle },
          hasTopLeftTransition: transitionsTouchTopLeft(node.transitions),
          children,
        };

        if (behavior) {
          const behaviorCtx = withCurrentNodeGeometry(ctx, {
            x: node.x,
            y: node.y,
            width: node.width,
            height: node.height,
            rotation: node.rotation ?? 0,
          });
          const { patches, effects, continuations, runtimeDomTarget } =
            runCompiledNodeBehavior(behavior, behaviorCtx, node.id);

          for (const patch of patches) {
            renderNode = applyPatchToContainerNode(renderNode, patch);
          }

          ctx.__runtimeEffects?.push(...effects);
          ctx.__runtimeContinuations?.push(...continuations);
          renderNode = applyRuntimeDomTargetFlags(
            renderNode,
            runtimeDomTarget,
            behavior,
          );
        }

        if (renderNode.style.visibility === false) {
          return null;
        }

        if (renderNode.layout !== "flex") {
          renderNode = {
            ...renderNode,
            children: prepareAbsoluteContainerChildren(
              renderNode.children,
              renderNode.x,
              renderNode.y,
              renderNode.id,
              renderNode.width,
              renderNode.height,
              renderNode.zIndex,
            ),
          };
        }

        return renderNode;
      });
    },
  };
}
